import { API_CONFIG, STORAGE_KEYS, HTTP_STATUS } from './api.config';
import type { RequestConfig, ApiResponse } from './api.types';
import { ApiError } from './api.types';
import { CODIGO_ESTABLECIMIENTO_NO_AUTORIZADO, EVENTO_ESTABLECIMIENTO_NO_AUTORIZADO } from '../../shared/workspace/eventosWorkspace';
import { toastBridge } from './toast-bridge';
import { planVencidoBridge } from './plan-vencido-bridge';
import { storageService } from '../../utils/storage';

const METODOS_ESCRITURA = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const RUTAS_EXENTAS_PLAN_VENCIDO = [
  '/usuarios/login',
  '/usuarios/register',
  '/usuarios/refresh-token',
  '/usuarios/logout',
  '/suscripciones/estados-lote',
];

class HttpClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string = API_CONFIG.BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getStorageValue(key: string): string | null {
    return localStorage.getItem(key);
  }

  private clearAuthStorage(): void {
    [
      STORAGE_KEYS.TOKEN,
      STORAGE_KEYS.REFRESH_TOKEN,
      STORAGE_KEYS.USER,
      STORAGE_KEYS.AUTH_STORE
    ].forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}/api/${API_CONFIG.API_VERSION}${endpoint}`, window.location.origin);

    const empresaId = this.getStorageValue(STORAGE_KEYS.EMPRESA_ID);
    if (empresaId) {
      url.searchParams.set('empresaId', empresaId);
    }

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private getHeaders(customHeaders?: Record<string, string>): Headers {
    const headers = new Headers({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    const token = this.getStorageValue(STORAGE_KEYS.TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const empresaId = this.getStorageValue(STORAGE_KEYS.EMPRESA_ID);
    if (empresaId) {
      headers.set('X-Empresa-Id', empresaId);
    }

    const establecimientoId = this.getStorageValue(STORAGE_KEYS.ESTABLECIMIENTO_ID);
    if (establecimientoId) {
      headers.set('X-Establecimiento-Id', establecimientoId);
    }

    return headers;
  }

  private notifyToast(error: ApiError): void {
    const { status } = error;
    if (status === 0) {
      toastBridge.error('Sin conexión con el servidor. Verifica que el backend esté activo.', 'network');
    } else if (status === HTTP_STATUS.FORBIDDEN) {
      toastBridge.warning('No tienes permisos para realizar esta acción.', 'forbidden');
    } else if (status === 408) {
      toastBridge.warning('Tiempo de espera agotado. Verifica tu conexión.', 'timeout');
    } else if (status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      toastBridge.error('Error en el servidor. Intenta nuevamente más tarde.', 'server-error');
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const rawData = isJson ? await response.json() : null;

    const toStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined;

    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      const errorMessage = toStr(rawData?.mensaje) || toStr(rawData?.message) || toStr(rawData?.error) || toStr(rawData?.Message) || 'Credenciales inválidas';
      throw new ApiError(errorMessage, HTTP_STATUS.UNAUTHORIZED);
    }

    if (response.status === HTTP_STATUS.FORBIDDEN && rawData?.codigoError === CODIGO_ESTABLECIMIENTO_NO_AUTORIZADO) {
      window.dispatchEvent(new CustomEvent(EVENTO_ESTABLECIMIENTO_NO_AUTORIZADO));
    }

    if (!response.ok) {
      const errorMessage = toStr(rawData?.mensaje) || toStr(rawData?.message) || toStr(rawData?.error) || toStr(rawData?.Message) || toStr(rawData?.title) || `Error HTTP ${response.status}`;
      throw new ApiError(errorMessage, response.status, rawData?.errors);
    }

    const isBackendResponse = rawData && typeof rawData === 'object' && 'exito' in rawData && 'data' in rawData;
    const extractedData = isBackendResponse ? rawData.data : rawData;

    return {
      data: extractedData as T,
      status: response.status,
      ok: response.ok,
      mensaje: isBackendResponse ? rawData.mensaje : undefined,
      codigoError: isBackendResponse ? rawData.codigoError : undefined,
      paginacion: isBackendResponse && rawData.paginacion ? rawData.paginacion : undefined,
    };
  }

  private async tryRefreshToken(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const refreshToken = this.getStorageValue(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) return false;

        const response = await fetch(`${this.baseUrl}/api/${API_CONFIG.API_VERSION}/usuarios/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        const tokenData = data.data || data;

        if (tokenData.token && tokenData.refreshToken) {
          const expiresAt = Date.now() + (tokenData.expiresIn || 28800) * 1000;
          localStorage.setItem(STORAGE_KEYS.TOKEN, tokenData.token);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokenData.refreshToken);
          localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
          return true;
        }

        return false;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private estadoBloqueoPlan(method: string, endpoint: string): 'ninguno' | 'lectura' | 'inhabilitada' {
    if (RUTAS_EXENTAS_PLAN_VENCIDO.some(ruta => endpoint.includes(ruta))) return 'ninguno';
    if (method === 'POST' && endpoint.split('?')[0] === '/empresas') return 'ninguno';

    const suscripcion = storageService.getSuscripcion();
    if (suscripcion?.activo !== false) return 'ninguno';

    if (suscripcion.enPeriodoGracia !== true) return 'inhabilitada';
    return METODOS_ESCRITURA.has(method) ? 'lectura' : 'ninguno';
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const { method = 'GET', headers, body, params, timeout = API_CONFIG.TIMEOUT, signal } = config;

    const bloqueo = this.estadoBloqueoPlan(method, endpoint);
    if (bloqueo === 'inhabilitada') {
      const empresaActualId = this.getStorageValue(STORAGE_KEYS.EMPRESA_ID);
      const suscripciones = storageService.getSuscripciones();
      const tieneOtraEmpresaActiva = storageService.getEmpresas().some(
        e => e.id !== empresaActualId && e.esActivo && suscripciones[e.id]?.activo !== false,
      );
      toastBridge.error(
        tieneOtraEmpresaActiva
          ? 'Esta empresa no tiene acceso activo a Senciyo. Cambia a otra de tus empresas con un plan activo para continuar.'
          : 'Esta empresa no tiene acceso activo a Senciyo. Crea una nueva empresa para seguir usando Senciyo.',
        'empresa-inhabilitada',
      );
      throw new ApiError('Empresa inhabilitada', 403);
    }
    if (bloqueo === 'lectura') {
      const suscripcion = storageService.getSuscripcion();
      planVencidoBridge.mostrar(suscripcion?.estado === 'Pagado' ? 'pagado' : 'trial');
      throw new ApiError('Tu plan ha vencido', 402);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const url = this.buildUrl(endpoint, params);

    let finalBody = body;
    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      const empresaId = this.getStorageValue(STORAGE_KEYS.EMPRESA_ID);
      if (empresaId && typeof body === 'object' && !Array.isArray(body)) {
        const bodyObj = body as Record<string, unknown>;
        if (!bodyObj.empresaId) {
          finalBody = { ...bodyObj, empresaId };
        }
      }
    }

    try {
      const response = await fetch(url, {
        method,
        headers: this.getHeaders(headers),
        body: finalBody ? JSON.stringify(finalBody) : undefined,
        signal: signal || controller.signal,
      });

      if (response.status === HTTP_STATUS.UNAUTHORIZED && !endpoint.includes('/refresh-token') && !endpoint.includes('/login')) {
        const refreshed = await this.tryRefreshToken();
        
        if (refreshed) {
          const retryResponse = await fetch(url, {
            method,
            headers: this.getHeaders(headers),
            body: finalBody ? JSON.stringify(finalBody) : undefined,
            signal: signal || controller.signal,
          });
          
          const result = await this.handleResponse<T>(retryResponse);
          clearTimeout(timeoutId);
          return result;
        } else {
          this.clearAuthStorage();
          throw new ApiError('Sesión expirada', HTTP_STATUS.UNAUTHORIZED);
        }
      }

      return await this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        this.notifyToast(error);
        throw error;
      }

      let apiError: ApiError;
      if (error instanceof Error && error.name === 'AbortError') {
        apiError = new ApiError('La solicitud ha excedido el tiempo de espera', 408);
      } else if (error instanceof Error) {
        apiError = new ApiError(error.message, 0);
      } else {
        apiError = new ApiError('Error de red', 0);
      }

      this.notifyToast(apiError);
      throw apiError;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, body?: unknown, params?: Record<string, string | number | boolean | undefined>, timeout?: number): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body, params, timeout });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'PATCH', body, params });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async obtenerBlob(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<Blob> {
    const url = this.buildUrl(endpoint, params);
    const response = await fetch(url, { headers: this.getHeaders() });
    if (!response.ok) throw new Error(`Error HTTP ${response.status}`);
    return response.blob();
  }
}

export const httpClient = new HttpClient();
export { HttpClient };
